import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import * as api from '../services/api';

const SettingsContext = createContext(null);

const SETTINGS_CACHE_KEY = 'datacrm_settings_cache';

const defaultSettings = {
  appName: 'DataCRM',
  appTagline: 'Gestión Inteligente',
  logoUrl: '',
  faviconUrl: '',
  primaryColor: '#00b30c',
  secondaryColor: '#64f288',
  accentColor: '#71f75f',
  currency: 'EUR',
  currencySymbol: '€',
};

// Try to get cached settings immediately
const getCachedSettings = () => {
  try {
    const cached = localStorage.getItem(SETTINGS_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      return { ...defaultSettings, ...parsed };
    }
  } catch (e) {
    console.log('No cached settings found');
  }
  return defaultSettings;
};

// Save settings to cache
const cacheSettings = (settings) => {
  try {
    localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify(settings));
  } catch (e) {
    console.log('Could not cache settings');
  }
};

// Apply theme immediately on load (before React mounts)
const applyThemeImmediate = (settings) => {
  const root = document.documentElement;
  
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result 
      ? `${parseInt(result[1], 16)} ${parseInt(result[2], 16)} ${parseInt(result[3], 16)}`
      : null;
  };

  if (settings.primaryColor) {
    root.style.setProperty('--color-primary', settings.primaryColor);
    const rgb = hexToRgb(settings.primaryColor);
    if (rgb) root.style.setProperty('--color-primary-rgb', rgb);
  }
  if (settings.secondaryColor) {
    root.style.setProperty('--color-secondary', settings.secondaryColor);
  }
  if (settings.accentColor) {
    root.style.setProperty('--color-accent', settings.accentColor);
  }
};

// Apply cached theme immediately
const cachedSettings = getCachedSettings();
applyThemeImmediate(cachedSettings);

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(cachedSettings);
  const [loading, setLoading] = useState(true);

  // Apply CSS variables for colors
  const applyTheme = useCallback((newSettings) => {
    const root = document.documentElement;
    
    // Convert hex to RGB for Tailwind CSS variables
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result 
        ? `${parseInt(result[1], 16)} ${parseInt(result[2], 16)} ${parseInt(result[3], 16)}`
        : null;
    };

    // Set CSS custom properties
    if (newSettings.primaryColor) {
      root.style.setProperty('--color-primary', newSettings.primaryColor);
      const rgb = hexToRgb(newSettings.primaryColor);
      if (rgb) root.style.setProperty('--color-primary-rgb', rgb);
    }
    if (newSettings.secondaryColor) {
      root.style.setProperty('--color-secondary', newSettings.secondaryColor);
    }
    if (newSettings.accentColor) {
      root.style.setProperty('--color-accent', newSettings.accentColor);
    }

    // Update favicon if provided
    if (newSettings.faviconUrl) {
      const favicon = document.querySelector("link[rel*='icon']") || document.createElement('link');
      favicon.type = 'image/x-icon';
      favicon.rel = 'shortcut icon';
      favicon.href = newSettings.faviconUrl;
      document.head.appendChild(favicon);
    }

    // Update page title
    if (newSettings.appName) {
      document.title = newSettings.appName;
    }
  }, []);

  // Load settings from backend
  const loadSettings = useCallback(async () => {
    try {
      const data = await api.getSettings();
      if (data && Object.keys(data).length > 0) {
        const mergedSettings = { ...defaultSettings, ...data };
        setSettings(mergedSettings);
        applyTheme(mergedSettings);
        // Cache the new settings for next load
        cacheSettings(mergedSettings);
      }
    } catch (error) {
      console.log('Using cached/default settings');
    } finally {
      setLoading(false);
    }
  }, [applyTheme]);

  // Reload settings (called after saving)
  const reloadSettings = useCallback(async () => {
    await loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  return (
    <SettingsContext.Provider value={{ settings, loading, reloadSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export default SettingsContext;
